import { useRef, useState } from 'react'
import Button from './Button'

export default function FileUpload({
  onUpload,
  accept = '.pdf,.txt,.md,.doc,.docx',
  label,
}: {
  onUpload: (file: File) => Promise<void>
  accept?: string
  label: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFile = async (file: File) => {
    setError(null)
    setUploading(true)
    try {
      await onUpload(file)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          const file = e.dataTransfer.files[0]
          if (file) handleFile(file)
        }}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragging ? 'border-primary bg-primary-light' : 'border-border-subtle hover:border-border-subtle-hover'
        }`}
      >
        <p className="text-text-muted text-sm mb-3">{label}</p>
        <p className="text-xs text-text-faint mb-4">Drop file here or</p>
        <Button
          variant="secondary"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Select file'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleFile(file)
          }}
        />
      </div>
      {error && <p className="text-accent-error text-xs">{error}</p>}
    </div>
  )
}
